import { useCallback } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import { useUpdateAppointment } from '@/services/scheduling/use-update-appointment';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import {
  buildEditRecurringPayload,
  type RecurringScope,
} from '@/scheduling/components/appointments/edit-recurring-scope-dialog';
import type {
  CreateAppointmentRequest,
  PreLoadAppointmentRequest,
  PreLoadAppointmentResponse,
  CreateAppointmentProfileRequest,
} from '@/types/scheduling-api';

/**
 * Series-wide reschedule helper for recurring appointments.
 *
 * Mirrors the flow at `scheduling-page.tsx:501-586` (`fireSeriesReschedule`)
 * which was Samer-reviewed at PR #123 §4 and curl-verified at PR #100.
 * Extracted as a reusable hook so the popup-footer Reschedule button +
 * panel DateTimeSection edit pencil pick up the same flow as drag-drop.
 *
 * Flow:
 *   1. Pull patient / provider / profile IDs off the cached row.
 *   2. Pre-fetch the full appointment via `PreLoadAppointmentDetails`
 *      so the PUT body carries every nested field the backend validates.
 *      `fetchQuery` serves from cache when fresh, otherwise issues the
 *      preload POST.
 *   3. Overlay the new `start` / `end` onto the loaded body.
 *   4. Fire `useUpdateAppointment` with `editRecurring` payload built
 *      from the scope. `dataUpdate: ['start','end']` is the field list
 *      the backend propagates across the series.
 *
 * Caller is responsible for ONLY firing this for `following` / `all`
 * scopes — `this-only` uses `useRescheduleAppointment` (the focused
 * `/api/Appointment/Reschedule` endpoint) instead.
 */

/** Narrow participant shape — only what `useSeriesReschedule` reads
 *  (referenceType + patient.id + provider.id). Full `ParticipantResponse`
 *  from the wire-typed responses satisfies this structurally, so panel /
 *  popup / list-row callers can pass their participant array verbatim
 *  AND the `frame-scheduling.tsx` row-override path can synthesize an
 *  array from a `DayListRow` without an `as never` cast. PROD-858. */
export interface SeriesRescheduleParticipant {
  referenceType?: string | null;
  patient?: { id?: string | null } | null | undefined;
  provider?: { id?: string | null } | null | undefined;
}

/** Minimal source shape — anything carrying these fields can drive
 *  a series reschedule. Keeps the hook decoupled from the wider
 *  `AppointmentResponse` so panel/popup AND list-row callers can use it. */
export interface SeriesRescheduleSource {
  id: string;
  start?: string | null;
  participant?: SeriesRescheduleParticipant[] | null;
  appointmentProfile?: Pick<CreateAppointmentProfileRequest, 'id'> | null;
}

export interface SeriesRescheduleHook {
  /**
   * Pre-fetches the full appointment body, then fires the PUT with
   * `editRecurring`. Returns true on success, false on missing-context
   * or mutation failure (toast already surfaced).
   */
  fire: (
    apt: SeriesRescheduleSource,
    newStartISO: string,
    newEndISO: string,
    scope: Exclude<RecurringScope, 'this-only'>,
  ) => Promise<boolean>;
  isPending: boolean;
}

export function useSeriesReschedule(): SeriesRescheduleHook {
  const queryClient = useQueryClient();
  const updateAppointment = useUpdateAppointment();

  const fire = useCallback<SeriesRescheduleHook['fire']>(
    async (apt, newStartISO, newEndISO, scope) => {
      const patientId = findPatientParticipant(apt.participant)?.patient?.id;
      const providerId = apt.participant?.find((p) => {
        const t = p.referenceType?.toLowerCase();
        return t === 'practitioner' || t === 'provider';
      })?.provider?.id;
      const profileId = apt.appointmentProfile?.id;

      if (!patientId || !providerId || !profileId) {
        toast.error(
          "Couldn't reschedule series — appointment is missing patient / provider / profile context.",
        );
        return false;
      }

      const dismissLoading = toast.loading('Rescheduling series…');
      try {
        // Pre-fetch the full appointment body so the PUT carries every
        // nested field the backend may validate. `fetchQuery` serves
        // from cache when fresh and otherwise issues the preload POST.
        // Verified-curl shape at PR #100.
        const fullPreload = await queryClient.fetchQuery<PreLoadAppointmentResponse>({
          queryKey: ['appointment-details', apt.id, patientId, providerId, profileId],
          queryFn: ({ signal }) =>
            post<PreLoadAppointmentResponse>(
              '/api/v1/Practice/PreLoadAppointmentDetails',
              {
                appointmentId: apt.id,
                patientId,
                providerId,
                billing: {},
                patient: {},
                appointmentProfileId: profileId,
              } as PreLoadAppointmentRequest,
              { signal },
            ),
        });

        const fullAppt = fullPreload.appointment;
        if (!fullAppt) {
          toast.error("Couldn't reschedule series — preload returned no appointment.");
          return false;
        }

        // dataUpdate is the field-name list backend reads to decide
        // WHICH columns get propagated to other occurrences. Other body
        // fields are ignored for the propagation step (they stay on the
        // source occurrence only). For a reschedule, the propagated
        // fields are `start` + `end` — matches Flutter convention.
        const editRecurring = buildEditRecurringPayload(
          scope,
          apt.start ?? undefined,
          ['start', 'end'],
        );

        // Re-normalise both fields to the no-`Z` local wall-clock the .NET
        // model binder expects. Callers hand us `toISOString()` output (UTC
        // with a trailing `Z`); the backend strips the `Z` and binds the
        // literal, so an un-normalised value lands N hours early (PROD-1752:
        // 13:00 Asia/Dubai saved as 09:00). This matches the two sibling
        // write paths — book (`transform-form-to-request.ts`) and single
        // reschedule (`use-reschedule-appointment.ts`). `formatLocalDatetimeForWire`
        // is idempotent on an already-stripped string, so it's safe for any caller.
        const body = {
          ...(fullAppt as unknown as CreateAppointmentRequest),
          start: formatLocalDatetimeForWire(newStartISO),
          end: formatLocalDatetimeForWire(newEndISO),
        } as CreateAppointmentRequest;

        await updateAppointment.mutateAsync({
          appointmentId: apt.id,
          body,
          editRecurring,
        });
        toast.success('Series rescheduled');
        return true;
      } catch {
        toast.error("Couldn't reschedule series — try again.");
        return false;
      } finally {
        toast.dismiss(dismissLoading);
      }
    },
    [queryClient, updateAppointment],
  );

  return { fire, isPending: updateAppointment.isPending };
}
